import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'

/* ================================================================== */
/*  Feedback & overlay pieces: notices, toast, modal, tabs, accordion. */
/* ================================================================== */

/* ---- Notice ----------------------------------------------------------- */
export type NoticeTone = 'info' | 'success' | 'warning'

const NOTICE_TONE: Record<NoticeTone, { box: string; dot: string }> = {
  info: { box: 'border-cobalt/40 bg-cobalt/10', dot: 'bg-cobalt' },
  success: { box: 'border-lc-accent/40 bg-lc-accent/10', dot: 'bg-lc-accent' },
  warning: { box: 'border-coral/40 bg-coral/10', dot: 'bg-coral' },
}

export function Notice({ tone = 'info', title, className, children }: { tone?: NoticeTone; title?: string; className?: string; children: React.ReactNode }) {
  const t = NOTICE_TONE[tone]
  return (
    <div role="status" className={cn('flex gap-3 rounded-panel border p-4', t.box, className)}>
      <span aria-hidden="true" className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', t.dot)} />
      <div className="text-[14px] leading-relaxed text-lc-ink">
        {title && <p className="font-bold">{title}</p>}
        <div className={cn(title && 'mt-0.5', 'text-lc-mute')}>{children}</div>
      </div>
    </div>
  )
}

/* ---- Toast ------------------------------------------------------------- */
export function Toast({ open, onDone, duration = 2600, children }: { open: boolean; onDone?: () => void; duration?: number; children: React.ReactNode }) {
  useEffect(() => {
    if (!open || !onDone) return
    const t = window.setTimeout(onDone, duration)
    return () => window.clearTimeout(t)
  }, [open, onDone, duration])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 24, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 12, opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 320, damping: 24 }}
          className="fixed inset-x-0 bottom-8 z-[110] flex justify-center px-4"
          role="status"
        >
          <div className="lc-glass flex items-center gap-3 rounded-pill py-3 pl-5 pr-6 text-[15px] font-semibold text-lc-ink shadow-pop">
            <span aria-hidden="true" className="grid h-5 w-5 place-items-center rounded-full bg-lime">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#131118" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
            </span>
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/* ---- Modal ------------------------------------------------------------- */
const MODAL_WIDTH: Record<'md' | 'lg' | 'xl', string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  theme = 'dark',
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  size?: 'md' | 'lg' | 'xl'
  /* The modal portals to <body>, escaping the page's theme wrapper, so it
     carries its own: light on cream community pages, dark on night pages. */
  theme?: 'dark' | 'light'
  children: React.ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    // Lock the page while the modal is open so only the modal scrolls.
    const html = document.documentElement
    const prevOverflow = html.style.overflow
    html.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      html.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // data-lenis-prevent: keep the smooth-scroller from hijacking the wheel
          // so scrolling happens inside the modal, not the page behind it.
          data-lenis-prevent
          className={cn(
            'fixed inset-0 z-[130] flex items-center justify-center overflow-y-auto overscroll-contain p-4 backdrop-blur-sm sm:p-6',
            theme === 'light' ? 'lc-light bg-night-900/40' : 'lc-dark bg-night-900/70',
          )}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ scale: 0.92, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            className={cn('lc-menu my-auto flex max-h-[88vh] w-full flex-col rounded-card p-5 shadow-pop sm:p-7', MODAL_WIDTH[size])}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-6">
              <h3 className="font-display text-xl text-lc-ink sm:text-2xl">{title}</h3>
              <button
                onClick={onClose}
                aria-label="Close"
                className="lc-glass-soft grid h-9 w-9 shrink-0 place-items-center rounded-full text-lc-mute transition-colors hover:text-lc-ink"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
              </button>
            </div>
            {/* transform-gpu: force this scroll region onto its own compositing
                layer. Without it, mobile Safari/Chrome sometimes fail to paint
                content below the fold (e.g. the action buttons) until a tap or
                scroll triggers a repaint — the "buttons don't show until I click"
                bug. A GPU layer makes the whole panel paint up front. */}
            <div className="transform-gpu -mr-2 mt-4 overflow-y-auto overscroll-contain pr-2">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

/* ---- Accordion ----------------------------------------------------------- */
export function Accordion({ items, className }: { items: { q: string; a: string }[]; className?: string }) {
  const [open, setOpen] = useState<number | null>(0)
  return (
    <div className={cn('lc-glass overflow-hidden rounded-card', className)}>
      {items.map((it, i) => {
        const isOpen = open === i
        return (
          <div key={it.q} className={cn(i > 0 && 'border-t border-lc-line')}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left"
            >
              <span className="font-display text-xl text-lc-ink">{it.q}</span>
              <span
                aria-hidden="true"
                className={cn('text-lc-mute transition-transform duration-300 ease-brand', isOpen && 'rotate-45')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              </span>
            </button>
            <div className={cn('grid transition-[grid-template-rows] duration-400 ease-brand', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
              <div className="overflow-hidden">
                <p className="px-6 pb-5 text-[15px] leading-relaxed text-lc-mute">{it.a}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
